import type {CSSProperties, ReactNode} from 'react'

import type {AuthHydroClickAttributes} from '../../marketing-header.types'

type AuthCTALinkProps = {
  /** SSR'd href (`login_path` / `signup_path` with attribution params, or `/` for Dashboard). */
  href: string

  children: ReactNode

  className: string

  /** Spread as `data-analytics-event` (marketing click tracking). */
  analyticsEvent: Record<string, string | undefined>

  /** Server-signed Hydro `authentication.click` payload, rendered as `data-hydro-click{,-hmac}`. */
  hydro?: AuthHydroClickAttributes

  /** `-1` while the mobile menu is open, to keep the CTA out of the trapped tab order. */
  tabIndex?: number

  style?: CSSProperties
}

/**
 * Shared presentational anchor for the Sign in / Sign up / Dashboard CTAs. Emits both the marketing
 * `data-analytics-event` and — when Rails passes the signed payload — the `data-hydro-click{,-hmac}`
 * attributes for the `authentication.click` funnel event. The HMAC can't be computed client-side, so
 * both Hydro attributes are omitted together when the payload is absent.
 */
export function AuthCTALink({href, children, className, analyticsEvent, hydro, tabIndex, style}: AuthCTALinkProps) {
  const hydroAttributes =
    hydro?.auth_hydro_click && hydro?.auth_hydro_click_hmac
      ? {'data-hydro-click': hydro.auth_hydro_click, 'data-hydro-click-hmac': hydro.auth_hydro_click_hmac}
      : {}

  return (
    <a href={href} className={className} tabIndex={tabIndex} style={style} {...analyticsEvent} {...hydroAttributes}>
      {children}
    </a>
  )
}
